import type { GatewayBrowserClient } from "../gateway.ts";
import type { SelfImprovementJobRecord } from "../types.ts";
import type { ChatSubagentCard } from "../ui-types.ts";
import type { SessionArtifactSummary } from "./chat-file-changes-summary.ts";

const SELF_IMPROVE_LABEL_PREFIX = "self-improve:";

export function parseSelfImprovementIssueFromLabel(label: string): string | null {
  const trimmed = label.trim();
  if (!trimmed.toLowerCase().startsWith(SELF_IMPROVE_LABEL_PREFIX)) {
    return null;
  }
  const issue = trimmed.slice(SELF_IMPROVE_LABEL_PREFIX.length).trim();
  return issue || null;
}

export function isSelfImprovementSubagentCard(card: ChatSubagentCard): boolean {
  const label = card.label?.trim() ?? "";
  return label.toLowerCase().startsWith(SELF_IMPROVE_LABEL_PREFIX);
}

/** Deduped repo-relative paths from subagent cards (explicit changedPaths only). */
export function collectChangedPathsFromCards(cards: ChatSubagentCard[]): string[] {
  const seen = new Set<string>();
  const out: string[] = [];
  for (const card of cards) {
    for (const raw of card.changedPaths ?? []) {
      const path = raw.trim().replace(/^\.\//, "");
      if (!path || seen.has(path)) {
        continue;
      }
      seen.add(path);
      out.push(path);
    }
  }
  return out.sort();
}

export function buildArtifactSummaryFromChangedPaths(
  paths: string[],
  workspace = "",
): SessionArtifactSummary | null {
  const normalized = [
    ...new Set(paths.map((p) => p.trim().replace(/^\.\//, "")).filter(Boolean)),
  ].sort();
  if (normalized.length === 0) {
    return null;
  }
  const files = normalized.map((path) => ({
    path,
    op: "modified" as const,
    additions: 0,
    deletions: 0,
  }));
  return {
    workspace,
    files,
    totals: {
      files: files.length,
      created: 0,
      modified: files.length,
      deleted: 0,
      additions: 0,
      deletions: 0,
    },
  };
}

export function mergeArtifactSummaries(
  primary: SessionArtifactSummary | null,
  fallback: SessionArtifactSummary | null,
): SessionArtifactSummary | null {
  if (primary && primary.totals.files > 0) {
    return primary;
  }
  return fallback;
}

export function indexSelfImprovementJobsByWorkerSession(
  jobs: SelfImprovementJobRecord[],
): Map<string, string[]> {
  const map = new Map<string, string[]>();
  for (const job of jobs) {
    const key = job.workerSessionKey?.trim();
    const paths = job.changedPaths?.filter((p) => p.trim()) ?? [];
    if (key && paths.length > 0) {
      map.set(key, paths);
    }
  }
  return map;
}

export function indexSelfImprovementJobsByIssue(
  jobs: SelfImprovementJobRecord[],
): Map<string, string[]> {
  const map = new Map<string, string[]>();
  for (const job of jobs) {
    const issue = job.youtrackIssueReadable?.trim() || job.youtrackIssueId?.trim();
    const paths = job.changedPaths?.filter((p) => p.trim()) ?? [];
    if (issue && paths.length > 0) {
      map.set(issue, paths);
    }
  }
  return map;
}

/** Resolve changed paths for a card: on-card, then job registry by session or issue label. */
export function resolveChangedPathsForCard(
  card: ChatSubagentCard,
  jobsBySession: Map<string, string[]>,
  jobsByIssue: Map<string, string[]>,
): string[] {
  if (card.changedPaths?.length) {
    return card.changedPaths;
  }
  const child = card.childSessionKey?.trim();
  if (child && jobsBySession.has(child)) {
    return jobsBySession.get(child) ?? [];
  }
  const issue = parseSelfImprovementIssueFromLabel(card.label ?? "");
  if (issue && jobsByIssue.has(issue)) {
    return jobsByIssue.get(issue) ?? [];
  }
  return [];
}

export async function fetchSelfImprovementJobs(
  client: GatewayBrowserClient,
  agentId: string,
): Promise<SelfImprovementJobRecord[]> {
  try {
    const res = await client.request<{ jobs?: SelfImprovementJobRecord[] }>(
      "fleet.selfImprovement.list",
      { agentId },
    );
    return Array.isArray(res.jobs) ? res.jobs : [];
  } catch {
    return [];
  }
}

export function enrichCardsWithJobChangedPaths(
  cards: ChatSubagentCard[],
  jobsBySession: Map<string, string[]>,
  jobsByIssue: Map<string, string[]>,
): ChatSubagentCard[] {
  return cards.map((card) => {
    const paths = resolveChangedPathsForCard(card, jobsBySession, jobsByIssue);
    if (paths.length === 0) {
      return card;
    }
    return { ...card, changedPaths: paths };
  });
}

export function formatChangedPathsMarkdown(paths: string[]): string[] {
  return paths.map((p) => `  - \`${p.trim().replace(/^\.\//, "")}\``);
}
